import { useMemo } from 'react';
import { useMemberDepositRanking } from '@/hooks/queries';
import { Avatar, CardSkeleton, ErrorState, SectionHead, StatLine, TableSkeleton } from '@/components/ui';
import { GrowthTag } from '@/components/GrowthTag';
import { formatBRL, formatBRLShort, formatNumber, formatPercent } from '@/lib/format';
import type { MemberDepositEntry, MemberDepositRanking as Ranking } from '@/lib/types';
import {
  averagePerDeposit, clientsLabel, depositQuery, depositsLabel, DepositToolbar, NoDeposits, previousPeriodLabel, typeSuffix, UnlinkedStat,
  type DepositFilters,
} from './DepositToolbar';
import { LeaderboardList, Podium, splitPodium, type BoardItem, type BoardSelect } from './Leaderboard';
import { PositionDelta } from './PositionDelta';

const openLabel = (name: string) => `Ver os clientes de ${name}`;

function detailsOf(entry: MemberDepositEntry): string[] {
  if (entry.depositTotal === 0) return ['Nenhum depósito no período'];
  return [clientsLabel(entry.leadCount), depositsLabel(entry.depositCount), `${formatPercent(entry.share, 1)} do depositado`];
}

const itemOf = (entry: MemberDepositEntry): BoardItem => ({
  id: entry.member.id,
  position: entry.position,
  name: entry.member.name,
  avatarColor: entry.member.avatarColor,
  photoUrl: entry.member.photoUrl,
  score: entry.depositTotal,
  scoreLabel: formatBRL(entry.depositTotal),
  details: detailsOf(entry),
  growth: entry.growth,
  previousLabel: formatBRL(entry.previousTotal),
  positionChange: entry.positionChange,
  inactive: !entry.member.active,
});

interface Props {
  filters: DepositFilters;
  onChange: (patch: Partial<DepositFilters>) => void;
  /** Abre os clientes do membro, no ranking de clientes. */
  onOpenMember: (memberId: string) => void;
}

/**
 * Os membros do comercial pelo depositado dos clientes deles — a visão padrão
 * do ranking de depósitos, no mesmo pódio do ranking de membros. Abrir um
 * membro leva aos clientes dele, do maior depósito para o menor.
 */
export function MemberDepositRanking({ filters, onChange, onOpenMember }: Props) {
  const { data, isLoading, error } = useMemberDepositRanking(depositQuery(filters));
  const items = useMemo(() => (data?.entries ?? []).map(itemOf), [data]);

  if (error) return <ErrorState message={(error as Error).message} />;

  const { podium, rest } = splitPodium(items);
  const select: BoardSelect = { label: (item) => openLabel(item.name), onSelect: (item) => onOpenMember(item.id) };

  return (
    <>
      <DepositToolbar filters={filters} onChange={onChange} />

      {isLoading || !data ? (
        filters.view === 'table' ? <TableSkeleton rows={8} columns={9} /> : (
          <div className="grid grid3"><CardSkeleton height={220} /><CardSkeleton height={260} /><CardSkeleton height={200} /></div>
        )
      ) : (
        <>
          <Totals data={data} />

          {podium.length === 0 ? (
            <NoDeposits label={data.period.label} period={filters.period} onPeriod={(period) => onChange({ period })} />
          ) : filters.view === 'table' ? (
            <MemberDepositTable entries={data.entries} onOpen={onOpenMember} />
          ) : (
            <>
              <Podium items={podium} select={select} previousPeriod={previousPeriodLabel(data)} />
              {rest.length > 0 ? (
                <section>
                  <SectionHead title={`Classificação · ${items.length} ${items.length === 1 ? 'membro' : 'membros'}`} />
                  <LeaderboardList items={rest} leaderScore={podium[0]?.score ?? 0} select={select} previousPeriod={previousPeriodLabel(data)} />
                </section>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
}

function Totals({ data }: { data: Ranking }) {
  const { totals } = data;
  return (
    <div className="metric-row cols-5" style={{ marginBottom: 34 }}>
      <StatLine
        label={`Depositado · ${data.period.label}${typeSuffix(data.type)}`}
        value={formatBRLShort(totals.depositTotal)}
        color="var(--emphasis)"
        sub={<GrowthTag growth={totals.growth} previousLabel={formatBRL(totals.previousDepositTotal)} />}
      />
      <StatLine
        label="Depósitos"
        value={formatNumber(totals.depositCount)}
        sub={`Média ${formatBRLShort(averagePerDeposit(totals.depositTotal, totals.depositCount))} por depósito`}
      />
      <StatLine label="Clientes" value={formatNumber(totals.leadCount)} sub={`Média ${formatBRLShort(totals.averagePerLead)} por cliente`} />
      <UnlinkedStat total={totals.unlinkedTotal} count={totals.unlinkedCount} />
      <StatLine label="Membros depositando" value={`${totals.rankedMembers}/${totals.memberCount}`} />
    </div>
  );
}

/** A tabela de quem confere: o maior cliente de cada membro e o que ficou sem cliente. */
function MemberDepositTable({ entries, onOpen }: { entries: MemberDepositEntry[]; onOpen: (memberId: string) => void }) {
  return (
    <div className="table-wrap">
      <table className="tbl tbl-cards">
        <thead>
          <tr>
            <th style={{ width: 52 }}>#</th>
            <th>Membro</th>
            <th className="right">Depositado</th>
            <th className="right">Depósitos</th>
            <th className="right">Clientes</th>
            <th title="O cliente que mais depositou com o membro no período">Maior cliente</th>
            <th className="right" title="Depósitos sem cliente vinculado: contam para o membro, mas ficam fora do ranking de clientes">Sem cliente</th>
            <th className="right">Part.</th>
            <th className="right">vs. anterior</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const scored = entry.depositTotal > 0;
            return (
              <tr key={entry.member.id} className="row-hover rank-row" data-place={scored ? entry.position : undefined}>
                <td className="cell-pos">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span className="num display rank-pos">{scored ? entry.position : '–'}</span>
                    <PositionDelta change={entry.positionChange} />
                  </span>
                </td>
                <td className="cell-main">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Avatar name={entry.member.name} color={entry.member.avatarColor} photo={entry.member.photoUrl} size={24} />
                    <button
                      type="button"
                      className="link-btn"
                      style={{ fontWeight: 500 }}
                      aria-label={openLabel(entry.member.name)}
                      title={openLabel(entry.member.name)}
                      onClick={() => onOpen(entry.member.id)}
                    >
                      {entry.member.name}
                    </button>
                    {!entry.member.active ? <span className="chip">inativo</span> : null}
                  </span>
                </td>
                <td className="num right emphasis cell-key" style={{ fontWeight: 500 }} data-label="Depositado">{formatBRL(entry.depositTotal)}</td>
                <td className="num right cell-third" data-label="Depósitos">{formatNumber(entry.depositCount)}</td>
                <td className="num right cell-third" data-label="Clientes">{formatNumber(entry.leadCount)}</td>
                <td className="cell-third" data-label="Maior cliente">
                  {/* O nome e, embaixo, o valor: lado a lado, o valor sumia no cartão do celular. */}
                  {entry.topLead ? (
                    <div style={{ maxWidth: 220, minWidth: 0 }}>
                      <div className="truncate" title={entry.topLead.name}>{entry.topLead.name}</div>
                      <div className="num muted" style={{ fontSize: 11.5 }}>{formatBRL(entry.topLead.depositTotal)}</div>
                    </div>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="num right muted cell-third" data-label="Sem cliente">{entry.unlinkedTotal > 0 ? formatBRL(entry.unlinkedTotal) : '—'}</td>
                <td className="num right muted cell-third" data-label="Part.">{formatPercent(entry.share, 1)}</td>
                <td className="right cell-third" data-label="vs. anterior">
                  {scored ? <GrowthTag growth={entry.growth} previousLabel={formatBRL(entry.previousTotal)} compact /> : <span className="muted">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
